import { useLayout } from '../../contexts/LayoutContext';
import { useLanguage } from '../../contexts/LanguageContext';

export default function VerticalNav({ currentPage, onNavigate, currentUser }) {
  const { sidebarCollapsed, toggleSidebar } = useLayout();
  const { t } = useLanguage();

  const navItems = [
    { 
      id: 'dashboard', 
      label: t('sidebar.dashboard'), 
      icon: '📊', 
      path: '/',
      description: t('sidebar.dashboardDesc')
    },
    { 
      id: 'global-insights', 
      label: t('sidebar.globalInsights'), 
      icon: '🌍', 
      path: '/global-insights',
      description: t('sidebar.globalInsightsDesc'),
      adminOnly: true,
      badge: 'Internal'
    },
    {
      id: 'divider-1',
      type: 'divider',
      label: t('sidebar.management')
    },
    { 
      id: 'admin', 
      label: t('sidebar.admin'), 
      icon: '⚙️', 
      path: '/admin',
      description: t('sidebar.adminDesc'),
      adminOnly: true
    }
  ];

  const filteredItems = navItems.filter(item => {
    if (item.adminOnly && currentUser?.tier !== 'admin') return false;
    return true;
  });

  return (
    <nav aria-label={t('sidebar.management')} className={`h-full bg-[var(--bg-elevated)] border-r border-[var(--border-default)] flex flex-col transition-all duration-300 ${
      sidebarCollapsed ? 'w-16' : 'w-64'
    }`}>
      <div className="p-4 border-b border-[var(--border-default)] flex items-center justify-between">
        {!sidebarCollapsed && (
          <div className="flex items-center gap-2">
            <span className="text-xl">📈</span>
            <span className="text-lg font-bold text-[var(--text-primary)]">ControlVitals</span>
          </div>
        )}
        <button
          type="button"
          onClick={toggleSidebar}
          className="p-2 hover:bg-[var(--surface-hover)] rounded-lg transition-colors text-[var(--text-muted)] hover:text-[var(--text-primary)]"
          title={sidebarCollapsed ? t('sidebar.expandMenu') : t('sidebar.collapseMenu')}
          aria-label={sidebarCollapsed ? t('sidebar.expandMenu') : t('sidebar.collapseMenu')}
          aria-expanded={!sidebarCollapsed}
        >
          {sidebarCollapsed ? '→' : '←'}
        </button>
      </div>

      <div className="flex-1 overflow-y-auto py-4">
        {filteredItems.map(item => {
          if (item.type === 'divider') {
            return (
              <div key={item.id} className="px-4 py-3">
                {!sidebarCollapsed && (
                  <span className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wider">
                    {item.label}
                  </span>
                )}
                {sidebarCollapsed && <div className="border-t border-[var(--border-default)]" />}
              </div>
            );
          }

          const isActive = currentPage === item.id || 
            (item.id === 'dashboard' && currentPage === 'home');

          return (
            <button
              type="button"
              key={item.id}
              onClick={() => onNavigate(item.path)}
              className={`w-full flex items-center gap-3 px-4 py-3 transition-all ${
                isActive 
                  ? 'bg-gradient-to-r from-purple-600/30 to-cyan-600/30 text-white border-l-2 border-cyan-400' 
                  : 'text-[var(--text-muted)] hover:bg-[var(--surface-hover)] hover:text-[var(--text-primary)]'
              } ${sidebarCollapsed ? 'justify-center' : ''}`}
              title={sidebarCollapsed ? item.label : undefined}
            >
              <span className="text-lg">{item.icon}</span>
              {!sidebarCollapsed && (
                <div className="flex-1 text-left">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{item.label}</span>
                    {item.badge && (
                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                        item.badge === 'Premium' 
                          ? 'bg-purple-500/20 text-purple-400' 
                          : 'bg-cyan-500/20 text-cyan-400'
                      }`}>
                        {item.badge}
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-[var(--text-muted)]">{item.description}</span>
                </div>
              )}
            </button>
          );
        })}
      </div>

      <div className="p-4 border-t border-[var(--border-default)]">
        {currentUser ? (
          <div className={`flex items-center gap-3 ${sidebarCollapsed ? 'justify-center' : ''}`}>
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-cyan-500 flex items-center justify-center text-white text-sm font-bold">
              {currentUser.username?.[0]?.toUpperCase() || 'U'}
            </div>
            {!sidebarCollapsed && (
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-[var(--text-primary)] truncate">
                  {currentUser.username}
                </div>
                <div className="text-xs text-[var(--text-muted)] capitalize">
                  {typeof currentUser.tier === 'object' ? currentUser.tier?.name || currentUser.tier?.slug : currentUser.tier}
                </div>
              </div>
            )}
          </div>
        ) : (
          !sidebarCollapsed && (
            <div className="text-sm text-[var(--text-muted)]">{t('sidebar.notLoggedIn')}</div>
          )
        )}
      </div>
    </nav>
  );
}
